// ──────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────

export interface ScoreboardConfig {
  autoStopOnScore: boolean
  customTeamCount: number
  customTeamNames: string[]
  teamStructure: ScoreboardTeamStructure
  timerDurationSeconds: number
  timerMode: ScoreboardTimerMode
}
export type ScoreboardPhase = 'playing' | 'setup'
export interface ScoreboardSyncState {
  config: ScoreboardConfig
  phase: ScoreboardPhase
  teams: ScoreboardTeam[]
  // True while the timer is actively counting. False when stopped,
  // when the game is in setup, when the countdown reaches 0, or right
  // after an autoStopOnScore-triggered stop.
  timerRunning: boolean
  // Current displayed timer reading. For countdown mode this drains
  // toward 0; for count-up it grows from 0. Always non-negative.
  // Computed locally from a process-local time anchor (see notes
  // below) — `timerStartedAt` is intentionally NOT synced because
  // performance.now() is process-local and not comparable across
  // devices. Each side instead re-derives its own anchor from the
  // received timerSeconds so both displays tick smoothly between
  // controller broadcasts.
  timerSeconds: number
}

export interface ScoreboardTeam {
  color: string
  name: string
  score: number
}

export type ScoreboardTeamStructure = 'boys-v-girls' | 'color-teams' | 'custom'

export type ScoreboardTimerMode = 'countdown' | 'countup' | 'none'

// ──────────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────────

export const SCOREBOARD_MAX_CUSTOM_TEAMS = 8

export const SCOREBOARD_COLOR_TEAMS: ScoreboardTeam[] = [
  { color: '#ef4444', name: 'Red', score: 0 },
  { color: '#3b82f6', name: 'Blue', score: 0 },
  { color: '#22c55e', name: 'Green', score: 0 },
  { color: '#eab308', name: 'Yellow', score: 0 },
]

export const SCOREBOARD_BOYS_GIRLS_TEAMS: ScoreboardTeam[] = [
  { color: '#3b82f6', name: 'Boys', score: 0 },
  { color: '#ec4899', name: 'Girls', score: 0 },
]

// Eight palette slots so a custom 8-team setup gets distinct chips
// without repeats. Picks are a mix of the brand palette and tertiary
// hues that contrast well against the dark stage background.
export const SCOREBOARD_CUSTOM_COLORS = [
  '#ef4444', // red
  '#3b82f6', // blue
  '#22c55e', // green
  '#eab308', // yellow
  '#a855f7', // purple
  '#f97316', // orange
  '#ec4899', // pink
  '#14b8a6', // teal
] as const

// 99:59 is the largest mm:ss the chip's display can fit cleanly. The
// counter freezes here in count-up mode rather than rolling into hh
// territory, where the digit string would push past the chip width.
const COUNT_UP_CEILING_SECONDS = 99 * 60 + 59

// Lower bound on the scheduler's setTimeout delay. The scheduler
// halves the time-until-the-next-second-boundary on each fire, so
// the gap between wakeups shrinks geometrically as we approach the
// crossing (approx. 500, 250, 125, 62, 31, 20ms). The floor stops
// the geometric series from collapsing into a tight loop right at
// the boundary, while still landing the integer-second update
// within ~floor ms of the real moment.
const TIMER_FLOOR_MS = 20

// ──────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────

function cloneForSync<T>(value: T): T {
  const rawValue = toRaw(value) as unknown

  if (rawValue === null || typeof rawValue !== 'object') {
    return rawValue as T
  }

  if (Array.isArray(rawValue)) {
    return rawValue.map(item => cloneForSync(item)) as T
  }

  const cloned: Record<string, unknown> = {}
  for (const [ key, nestedValue ] of Object.entries(rawValue as Record<string, unknown>)) {
    cloned[key] = cloneForSync(nestedValue)
  }
  return cloned as T
}

// Wall-clock helper. performance.now() is monotonic and isn't
// affected by system clock adjustments, which makes it the right
// primitive for measuring elapsed time within a single process.
// Date.now() jumps if the OS clock is corrected (NTP, manual
// change), which would visibly skip or rewind the timer. The
// trade-off is that performance.now() values aren't comparable
// across processes or devices: fine here because the controller's
// anchor isn't shared; each side derives its own from timerSeconds.
function nowMs(): number {
  return import.meta.client ? performance.now() : 0
}

// ──────────────────────────────────────────────────────────
// Store
// ──────────────────────────────────────────────────────────

export const useScoreboardStore = defineStore('scoreboard', () => {
  // ── Phase ──────────────────────────────────────────────
  const phase = ref<ScoreboardPhase>('setup')

  // ── Teams ──────────────────────────────────────────────
  const teams = ref<ScoreboardTeam[]>([])

  // ── Setup config ───────────────────────────────────────
  const config = ref<ScoreboardConfig>({
    autoStopOnScore: true,
    customTeamCount: 4,
    customTeamNames: Array.from({ length: SCOREBOARD_MAX_CUSTOM_TEAMS }, (_, i) => `Team ${i + 1}`),
    teamStructure: 'color-teams',
    timerDurationSeconds: 30,
    timerMode: 'none',
  })

  // ── Timer state ────────────────────────────────────────
  const timerSeconds = ref(0)
  const timerRunning = ref(false)
  // Process-local time anchor (performance.now() value) at which
  // the current run started. Null whenever the timer is stopped.
  // Each tick re-derives timerSeconds from `(nowMs() - timerStartedAt)`
  // rather than incrementing/decrementing the previous reading, so
  // missed ticks (background tab throttling, GC pause, debugger
  // pause, CPU contention) self-correct on the next tick instead of
  // accumulating drift.
  const timerStartedAt = ref<null | number>(null)

  // ── Timer scheduler (client-side only) ─────────────────
  // Self-rescheduling setTimeout that halves its delay each fire as
  // it approaches the next integer-second boundary, then resets and
  // halves toward the next one. The pattern keeps the display update
  // within ~TIMER_FLOOR_MS of the real second tick without paying a
  // fixed-rate poll between crossings.

  let timerHandle: null | ReturnType<typeof setTimeout> = null

  function clearScheduledTick() {
    if (timerHandle !== null) {
      clearTimeout(timerHandle)
      timerHandle = null
    }
  }

  function scheduleNextTick() {
    clearScheduledTick()
    if (!import.meta.client || timerStartedAt.value === null) {
      return
    }
    const elapsedMs = nowMs() - timerStartedAt.value
    const msIntoCurrentSecond = ((elapsedMs % 1000) + 1000) % 1000
    const msUntilBoundary = 1000 - msIntoCurrentSecond
    const delay = Math.max(TIMER_FLOOR_MS, Math.floor(msUntilBoundary / 2))
    timerHandle = setTimeout(handleTick, delay)
  }

  function handleTick() {
    timerHandle = null
    if (
      !timerRunning.value
      || phase.value !== 'playing'
      || config.value.timerMode === 'none'
      || timerStartedAt.value === null
    ) {
      return
    }
    recomputeTimerSeconds()
    if (timerRunning.value && timerStartedAt.value !== null) {
      scheduleNextTick()
    }
  }

  function recomputeTimerSeconds() {
    if (timerStartedAt.value === null) {
      return
    }
    const elapsedSeconds = (nowMs() - timerStartedAt.value) / 1000
    if (config.value.timerMode === 'countdown') {
      const remaining = Math.max(
        0,
        config.value.timerDurationSeconds - Math.floor(elapsedSeconds),
      )
      timerSeconds.value = remaining
      if (remaining === 0) {
        // Hit zero, freeze. Display stays at 0; the leader can keep
        // awarding scores, and the next tap on the chip restarts a
        // fresh round.
        timerRunning.value = false
        timerStartedAt.value = null
        clearScheduledTick()
      }
    } else if (config.value.timerMode === 'countup') {
      const counted = Math.floor(elapsedSeconds)
      if (counted >= COUNT_UP_CEILING_SECONDS) {
        timerSeconds.value = COUNT_UP_CEILING_SECONDS
        timerRunning.value = false
        timerStartedAt.value = null
        clearScheduledTick()
      } else {
        timerSeconds.value = counted
      }
    }
  }

  // ── Actions ────────────────────────────────────────────

  function buildTeams(): ScoreboardTeam[] {
    const structure = config.value.teamStructure
    if (structure === 'color-teams') {
      return SCOREBOARD_COLOR_TEAMS.map(t => ({ ...t }))
    }
    if (structure === 'boys-v-girls') {
      return SCOREBOARD_BOYS_GIRLS_TEAMS.map(t => ({ ...t }))
    }
    return Array.from({ length: config.value.customTeamCount }, (_, i) => ({
      color: SCOREBOARD_CUSTOM_COLORS[i % SCOREBOARD_CUSTOM_COLORS.length] ?? '#6b7280',
      name: config.value.customTeamNames[i] || `Team ${i + 1}`,
      score: 0,
    }))
  }

  function startGame() {
    teams.value = buildTeams()
    // The timer always lands on the game screen in a stopped state,
    // pre-staged at its starting reading. The leader taps the chip
    // to actually begin counting. This avoids the surprise of a
    // round having already started before everyone is ready, and
    // matches the "tap to start a fresh round" model the chip uses
    // for every subsequent stop / restart.
    timerSeconds.value = config.value.timerMode === 'countdown'
      ? config.value.timerDurationSeconds
      : 0
    timerRunning.value = false
    timerStartedAt.value = null
    clearScheduledTick()
    phase.value = 'playing'
  }

  function exitGame() {
    clearScheduledTick()
    timerRunning.value = false
    timerStartedAt.value = null
    timerSeconds.value = 0
    phase.value = 'setup'
  }

  function toggleTimer() {
    if (phase.value !== 'playing' || config.value.timerMode === 'none') {
      return
    }
    if (timerRunning.value) {
      // Tap while running stops the timer in place. The reading
      // sticks so the leader can read the moment, but it's a dead
      // chip until the next tap, which restarts from the beginning.
      timerRunning.value = false
      timerStartedAt.value = null
      clearScheduledTick()
      return
    }
    // Tap while stopped restarts the round: the clock snaps back to
    // its starting point (duration for countdown, 0 for count-up)
    // and begins ticking again from a fresh time anchor. There is
    // intentionally no resume-from-where-you-left-off concept, since
    // the leader-paced flow of this game treats each timed window
    // as a fresh round.
    timerSeconds.value = config.value.timerMode === 'countdown'
      ? config.value.timerDurationSeconds
      : 0
    timerStartedAt.value = nowMs()
    timerRunning.value = true
    scheduleNextTick()
  }

  function maybeAutoStopOnScore() {
    if (
      config.value.autoStopOnScore
      && timerRunning.value
      && config.value.timerMode !== 'none'
    ) {
      timerRunning.value = false
      timerStartedAt.value = null
      clearScheduledTick()
    }
  }

  function incrementScore(teamIndex: number) {
    const team = teams.value[teamIndex]
    if (!team) {
      return
    }
    team.score += 1
    maybeAutoStopOnScore()
  }

  function decrementScore(teamIndex: number) {
    const team = teams.value[teamIndex]
    if (!team || team.score <= 0) {
      return
    }
    team.score -= 1
    maybeAutoStopOnScore()
  }

  // ── Sync state ─────────────────────────────────────────

  function getSyncState(): ScoreboardSyncState {
    // Read every property through its reactive proxy so the deep
    // watch in useGamePresentation registers each one as a dep.
    // Building the snapshot via cloneForSync alone unwraps the
    // proxies via toRaw before the read happens, which means inner
    // mutations like `teams.value[0].score = n` never re-run this
    // getter and never reach the presenter unless some other tracked
    // value (e.g. the ticking timer) re-fires the watcher. Reading
    // through the proxy here, then writing plain literals, keeps the
    // result both reactive-aware on read and structured-clone-safe
    // on send.
    return {
      config: {
        autoStopOnScore: config.value.autoStopOnScore,
        customTeamCount: config.value.customTeamCount,
        customTeamNames: [ ...config.value.customTeamNames ],
        teamStructure: config.value.teamStructure,
        timerDurationSeconds: config.value.timerDurationSeconds,
        timerMode: config.value.timerMode,
      },
      phase: phase.value,
      teams: teams.value.map(t => ({
        color: t.color,
        name: t.name,
        score: t.score,
      })),
      timerRunning: timerRunning.value,
      timerSeconds: timerSeconds.value,
    }
  }

  function applySyncState(next: ScoreboardSyncState) {
    clearScheduledTick()
    config.value = cloneForSync(next.config)
    teams.value = cloneForSync(next.teams)
    phase.value = next.phase
    timerSeconds.value = next.timerSeconds
    timerRunning.value = next.timerRunning

    // Re-derive the time anchor from the received timerSeconds so
    // the local scheduler can keep computing fresh values until the
    // next sync arrives. Used in two contexts:
    //   1. Controller restoring from localStorage on a mid-round
    //      reload — anchors so the timer continues from where it
    //      was saved.
    //   2. Presenter receiving an incoming sync — anchors so the
    //      presenter renders smoothly at 1Hz between broadcasts
    //      (which only fire when the integer second crosses on the
    //      controller side).
    // performance.now() is process-local, so each side's anchor is
    // independent. Clock skew between devices doesn't matter: every
    // controller tick re-broadcasts the integer, which re-anchors
    // the presenter.
    if (
      phase.value === 'playing'
      && timerRunning.value
      && config.value.timerMode !== 'none'
    ) {
      const elapsedAtSync = config.value.timerMode === 'countdown'
        ? config.value.timerDurationSeconds - timerSeconds.value
        : timerSeconds.value
      timerStartedAt.value = nowMs() - elapsedAtSync * 1000
      scheduleNextTick()
    } else {
      timerStartedAt.value = null
    }
  }

  return {
    applySyncState,
    config,
    decrementScore,
    exitGame,
    getSyncState,
    incrementScore,
    phase,
    startGame,
    teams,
    timerRunning,
    timerSeconds,
    toggleTimer,
  }
})
